import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCountdown } from "./useCountdown";
import { LOGIN_STATES } from "./useLogin.types";
import type { LoginState } from "./useLogin.types";

export { LOGIN_STATES } from "./useLogin.types";
export type { LoginState } from "./useLogin.types";

const FIRST_RESEND_SEC = 60;
const TICK_MS = 500;

export function useLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login, verifyLoginCode, resendLoginCode } =
    useAuth();

  const [currentState, setCurrentState] = useState<LoginState>(
    LOGIN_STATES.DEFAULT,
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codeTicket, setCodeTicket] = useState<{
    loginTicket: string;
    maskedEmail: string;
  } | null>(null);
  const [triesLeft, setTriesLeft] = useState<number | undefined>();
  const [shake, setShake] = useState(false);
  const [verified, setVerified] = useState(false);
  const navTimer = useRef<number>();
  useEffect(() => () => window.clearTimeout(navTimer.current), []);
  const [resendIn, setResendIn] = useCountdown();

  const locked =
    currentState === LOGIN_STATES.TOO_MANY_TRIES ||
    currentState === LOGIN_STATES.TICKET_EXPIRED;

  const next = useMemo(
    () => new URLSearchParams(location.search).get("next") || "/",
    [location.search],
  );
  const sessionExpired = useMemo(
    () => new URLSearchParams(location.search).get("session_expired") === "1",
    [location.search],
  );

  const isEmailValid = email.includes("@") && email.includes(".");
  const isPasswordFilled = password.length >= 1;
  const isFormValid = isEmailValid && isPasswordFilled;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;
    setIsSubmitting(true);
    const res = await login(email, password);
    setIsSubmitting(false);
    if (!("reason" in res)) {
      if (res.kind === "code") {
        setCodeTicket(res);
        setResendIn(FIRST_RESEND_SEC);
        setCurrentState(LOGIN_STATES.CODE_STEP);
      } else navigate(next, { replace: true });
      return;
    }
    if (res.reason === "INVALID_CREDENTIALS")
      setCurrentState(LOGIN_STATES.INVALID_CREDENTIALS);
    else if (res.reason === "ACCOUNT_BLOCKED")
      setCurrentState(LOGIN_STATES.ACCOUNT_BLOCKED);
    else if (res.reason === "ACCOUNT_DISABLED")
      setCurrentState(LOGIN_STATES.ACCOUNT_DISABLED);
    else if (res.reason === "NOT_ACTIVATED")
      setCurrentState(LOGIN_STATES.NOT_ACTIVATED);
    else if (res.reason === "CODE_SEND_FAILED")
      setCurrentState(LOGIN_STATES.CODE_SEND_FAILED);
    else setCurrentState(LOGIN_STATES.INVALID_CREDENTIALS);
  };

  const backToPassword = () => {
    setCodeTicket(null);
    setPassword("");
    setResendIn(0);
    setCurrentState(LOGIN_STATES.DEFAULT);
  };

  const submitCode = async (code: string) => {
    if (!codeTicket || isSubmitting || verified || shake) return;
    setIsSubmitting(true);
    const res = await verifyLoginCode(codeTicket.loginTicket, code);
    setIsSubmitting(false);
    if (!("reason" in res)) {
      setVerified(true);
      navTimer.current = window.setTimeout(
        () => navigate(next, { replace: true }),
        TICK_MS,
      );
      return;
    }
    if (res.reason === "TOO_MANY_TRIES")
      setCurrentState(LOGIN_STATES.TOO_MANY_TRIES);
    else if (res.reason === "TICKET_EXPIRED")
      setCurrentState(LOGIN_STATES.TICKET_EXPIRED);
    else if (res.reason === "CODE_SEND_FAILED")
      setCurrentState(LOGIN_STATES.CODE_SEND_FAILED);
    else if (res.reason === "WRONG_CODE") {
      setTriesLeft(res.triesLeft);
      setCurrentState(LOGIN_STATES.WRONG_CODE);
      setShake(true);
    } else setCurrentState(LOGIN_STATES.SOMETHING_WRONG);
  };

  const endShake = useCallback(() => setShake(false), []);

  const resendCode = async () => {
    if (!codeTicket || resendIn > 0 || locked) return;
    const res = await resendLoginCode(codeTicket.loginTicket);
    if (!("reason" in res)) {
      setResendIn(res.retryAfterSec);
      setCurrentState(LOGIN_STATES.CODE_STEP);
      return;
    }
    if (res.reason === "RESEND_TOO_SOON") setResendIn(res.retryAfterSec ?? 0);
    else if (res.reason === "TICKET_EXPIRED")
      setCurrentState(LOGIN_STATES.TICKET_EXPIRED);
    else setCurrentState(LOGIN_STATES.CODE_SEND_FAILED);
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    currentState,
    isSubmitting,
    isEmailValid,
    isFormValid,
    next,
    sessionExpired,
    isAuthenticated,
    navigate,
    handleSubmit,
    codeTicket,
    triesLeft,
    shake,
    verified,
    endShake,
    resendIn,
    submitCode,
    resendCode,
    locked,
    backToPassword,
  };
}
